// AI Generated Component
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';

@Component({
    standalone: false,
    selector: 'form-global-policy-settings',
    template: `
<form #policyTypes="ngForm">
    <div class="row">
        <div class="col-48 outer-t-medium">
            <div class="col-sm-48 outer-b">
                <label translate>Display this policy as:</label>
                <div class="btn-group btn-group-justified" role="group">
                    <div class="btn-group" role="group">
                        <input type="radio"
                               [(ngModel)]="policy.mode"
                                name="policy_mode"
                                value="violation"
                               style="display:none;">
                        <button type="button"
                                class="btn btn-border-radius btn-secondary"
                                [class.active]="policy.mode === 'violation'"
                                (click)="policy.mode = 'violation'"
                                role="button">
                            <span class="fas fa-ban fa-2x outer-t-small"></span>
                            <p translate>Unwanted</p>
                        </button>
                    </div>
                    <div class="btn-group" role="group">
                        <input type="radio"
                               [(ngModel)]="policy.mode"
                               name="policy_mode"
                               value="required"
                               style="display:none;">
                        <button type="button"
                                class="btn btn-border-radius btn-secondary"
                                [class.active]="policy.mode === 'required'"
                                (click)="policy.mode = 'required'"
                                role="button">
                            <span class="fas fa-exclamation-triangle fa-2x outer-t-small"></span>
                            <p translate>Required</p>
                        </button>
                    </div>
                    <div class="btn-group" role="group">
                        <input type="radio"
                               [(ngModel)]="policy.mode"
                                name="policy_mode"
                                value="search"
                               style="display:none;">
                        <button type="button"
                                class="btn btn-border-radius btn-secondary"
                                [class.active]="policy.mode === 'search'"
                                (click)="policy.mode = 'search'"
                                role="button">
                            <span class="fas fa-search fa-2x outer-t-small"></span>
                            <p translate>Matches</p>
                        </button>
                    </div>
                </div>
            </div>

            <div class="col-48 outer-b-large">
                <label translate>Priority</label>
                <form-field-select returnValue="value"
                                   [options]="policyPriorityOptions"
                                   [(ngModel)]="policy.priority"
                                   name="priority">
                </form-field-select>
            </div>

            <div class="col-48 outer-b">
                <label for="scheduled"><span translate>Scheduled</span> <span class="fas fa-clock"></span></label>
                <form-field-material-switch
                        class="block pt-1"
                        name="scheduled"
                        [(ngModel)]="policy.scheduled">
                </form-field-material-switch>
                <div class="help-block mt-0">
                    <span translate>Do you want the policy to run every time your website is crawled?</span>
                </div>
            </div>

            <div class="col-48">
                <label translate>Choose where the policy should apply</label>
                <form-field-radio-in-line returnValue="value"
                                          [(ngModel)]="policy.target_all_domains"
                                          name="target_all_domains"
                                          [options]="domainOptions">
                </form-field-radio-in-line>
            </div>
            <div class="col-48 policy-domain-select" *ngIf="policy.target_all_domains === 'false'">
                <label for="domains" translate>Select domains and groups</label>
                <mon-domain-picker [value]="policy.targets" (valueChange)="setPolicyTargets($event)"></mon-domain-picker>
            </div>
        </div>
    </div>
</form>
    `,
})
export class FormGlobalPolicySettingsComponent implements OnInit {
    @Input() policy: any;

    policyPriorityOptions: any[] = [];
    domainOptions: any[] = [];
    tagOptions: any[] = [];
    areAllTagsSelected = false;

    constructor (
        private translateService: TranslateService,
        private sessionService: SessionService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    selectAllTags (): void {
        if (this.policy.tags.length === this.tagOptions.length) {
            this.policy.tags = [];
            this.areAllTagsSelected = false;
        } else {
            this.policy.tags = this.tagOptions.map(function (entry: any) {
                return entry.value;
            });
            this.areAllTagsSelected = true;
        }
    }

    checkSelectedTags (): void {
        this.areAllTagsSelected = this.tagOptions.length > 0 && this.tagOptions.length === this.policy.tags.length;
    }

    setPolicyTargets (payload: any): void {
        this.policy.targets = payload;
    }

    private activate (): void {
        this.policy.priority = this.policy.priority || 'low';
        this.policy.mode = this.policy.mode || 'violation';
        if (typeof this.policy.scheduled !== 'boolean') {
            this.policy.scheduled = true;
        }
        this.policy.tags = this.policy.tags || [];
        if (this.policy.target_all_domains !== 'false' && this.policy.target_all_domains !== 'true') {
            this.policy.target_all_domains = Boolean(this.policy.target_all_domains).toString();
        }

        if (!this.policy.targets) {
            this.setupPredefinedTarget();
        }

        this.setupPriorityOptions();
        this.setupDomainOptions();
        this.setupTagOptions();
        this.checkSelectedTags();
        if (!this.areAllTagsSelected && this.policy.tags.length === 0) {
            this.selectAllTags();
        }
    }

    private setupPriorityOptions (): void {
        this.policyPriorityOptions = [
            {
                name: this.translateService.getString('Low'),
                value: 'low',
            },
            {
                name: this.translateService.getString('Medium'),
                value: 'medium',
            },
            {
                name: this.translateService.getString('High'),
                value: 'high',
            },
        ];
    }

    private setupDomainOptions (): void {
        this.domainOptions = [
            {
                name: this.translateService.getString('Global - All Domains'),
                value: 'true',
            },
            {
                name: this.translateService.getString('Choose domains'),
                value: 'false',
            },
        ];
    }

    private setupTagOptions (): void {
        this.tagOptions = [
            {
                name: this.translateService.getString('Content Quality'),
                value: 'qa',
            },
            {
                name: this.translateService.getString('Accessibility'),
                value: 'accessibility',
            },
            {
                name: this.translateService.getString('SEO Issues'),
                value: 'seo',
            },
        ];
    }

    private setupPredefinedTarget (): void {
        if (this.sessionService.domainGroup && typeof this.sessionService.domainGroup === 'object') {
            this.policy.targets = [
                {
                    type: 'DomainGroup',
                    id: this.sessionService.domainGroup.id,
                },
            ];
        } else if (this.sessionService.domain && typeof this.sessionService.domain === 'object') {
            this.policy.targets = [
                {
                    type: 'Domain',
                    id: this.sessionService.domain.id,
                },
            ];
        }
    }
}
